import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../config";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import { ArrowLeft, AlertCircle, Package, Tag, Layers, Hash, Image, Edit, X } from "lucide-react";

const ViewProductForm = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [product, setProduct] = useState({
    productName: "",
    productDescription: "",
    productPrice: "",
    productCategory: "",
    productType: 1,
    productQuantity: 1,
  });
  const [productImages, setProductImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        // Fetch product details
        const response = await fetch(
          `${API_BASE_URL}/api/products/details-edit/${productId}`,
          {
            headers: {
              Authorization: `${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const productData = await response.json();
        setProduct({
          productName: productData.productName,
          productDescription: productData.description,
          productPrice: productData.price,
          productCategory: productData.categoryId,
          productType: productData.type,
          productQuantity: productData.quantity,
        });

        // Fetch product images
        const imageResponse = await fetch(
          `${API_BASE_URL}/api/product-images/product/${productId}`
        );
        if (imageResponse.ok) {
          const imageData = await imageResponse.json();
          setProductImages(imageData);
        }

      } catch (error) {
        console.error("Error fetching product data:", error);
        setError("ไม่สามารถดึงข้อมูลสินค้าได้");
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [productId, token]);

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="bg-red-50 p-6 rounded-xl flex items-center max-w-md shadow-sm">
          <AlertCircle className="h-6 w-6 text-red-500 mr-4 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  const mainImage = productImages.length > 0 ? productImages[0].imageUrl : null;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-red-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            ย้อนกลับ
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">รายละเอียดสินค้า</h1>
            <Link
              to={`/account/my-store/edit-product/${productId}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Edit size={18} />
              แก้ไขสินค้า
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Image Section */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-red-600" />
              รูปภาพสินค้า
            </h3>

            {mainImage ? (
              <div
                className="aspect-square bg-gray-100 rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openImageModal(mainImage)}
              >
                <img
                  src={`${API_BASE_URL}${mainImage}`}
                  alt={product.productName}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = "https://via.placeholder.com/400?text=No+Image"; }}
                />
              </div>
            ) : (
              <div className="aspect-square bg-gray-100 rounded-xl flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Package className="w-16 h-16 mx-auto mb-2" />
                  <p>ไม่มีรูปภาพ</p>
                </div>
              </div>
            )}

            {/* Thumbnail Gallery */}
            {productImages.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mt-4">
                {productImages.map((img, index) => (
                  <div
                    key={img.imageId || index}
                    className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity border-2 border-transparent hover:border-red-500"
                    onClick={() => openImageModal(img.imageUrl)}
                  >
                    <img
                      src={`${API_BASE_URL}${img.imageUrl}`}
                      alt={`${product.productName} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = "https://via.placeholder.com/100?text=N/A"; }}
                    />
                  </div>
                ))}
              </div>
            )}

            <p className="text-center text-sm text-gray-500 mt-3">
              คลิกที่รูปเพื่อดูขนาดเต็ม
            </p>
          </div>

          {/* Info Section */}
          <div className="space-y-4">
            {/* Product Name */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-5 h-5 text-red-600" />
                <label className="text-sm font-medium text-gray-500">ชื่อสินค้า</label>
              </div>
              <p className="text-xl font-semibold text-gray-900">{product.productName}</p>
            </div>

            {/* Price */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-5 h-5 text-red-600" />
                <label className="text-sm font-medium text-gray-500">ราคา</label>
              </div>
              <p className="text-3xl font-bold text-red-600">
                ฿{Number(product.productPrice).toLocaleString()}
              </p>
            </div>

            {/* Category & Type */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-5 h-5 text-red-600" />
                    <label className="text-sm font-medium text-gray-500">หมวดหมู่</label>
                  </div>
                  <p className="font-medium text-gray-900">
                    {PRODUCT_CATEGORIES.find(c => c.id === product.productCategory)?.name || "ไม่ระบุ"}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Hash className="w-5 h-5 text-red-600" />
                    <label className="text-sm font-medium text-gray-500">จำนวนคงเหลือ</label>
                  </div>
                  <p className="font-medium text-gray-900">{product.productQuantity} ชิ้น</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">รายละเอียดสินค้า</h3>
          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
            {product.productDescription || "ไม่มีรายละเอียด"}
          </p>
        </div>

        {/* Image Modal */}
        {showImageModal && selectedImage && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setShowImageModal(false)}
          >
            <button
              className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
              onClick={() => setShowImageModal(false)}
            >
              <X size={24} />
            </button>
            <img
              src={`${API_BASE_URL}${selectedImage}`}
              alt={product.productName}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewProductForm;
